import { z } from "zod";

import { SIGNATURE_PAGE_NUMBER_PROMPT, nullableSignaturePageNumberSchema } from "@/src/models/common-schema";
import { CarePlanDocumentType, CarePlanFollowUpAttempt } from "@/src/models/constants/document-types";
import { BODY_PART_LATERALITY_PROMPT } from "@/src/models/constants/prompt";
import { SERVICE_LINE_PROMPT, ServiceLine } from "@/src/models/constants/service-lines";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { locationSchema } from "@/src/models/location";
import { Patient, patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const carePlanTypeDisplay: Record<CarePlanDocumentType, string> = {
  [CarePlanDocumentType.CARE_PLAN]: "Care Plan",
  [CarePlanDocumentType.REMINDER]: "Care Plan Reminder",
};

export const carePlanSchema = z.object({
  patient: patientMinimalSchema,
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  ...DocumentDateSchema.shape,
  clinicName: z.string().nullable(),
  companyName: z.string().nullable(),
  documentType: z.string().nullable(),
  serviceLine: z.nativeEnum(ServiceLine).nullable().describe(SERVICE_LINE_PROMPT),
  signatureRequired: z.boolean().nullable().describe("Is the document requiring a signature?"),
  signaturePageNumber: nullableSignaturePageNumberSchema.describe(SIGNATURE_PAGE_NUMBER_PROMPT),
  followUpAttempt: z.nativeEnum(CarePlanFollowUpAttempt).nullable(),
  receivingLocation: locationSchema.nullable(),
  bodyPartLaterality: z.string().nullable().describe(BODY_PART_LATERALITY_PROMPT),
});

export type CarePlan = Omit<z.infer<typeof carePlanSchema>, "patient"> & {
  patient: Patient | null;
} & DatabaseDefaults;

export const carePlanShape: CarePlan = {
  id: "",
  patient: null,
  provider: null,
  receivingProvider: null,
  dateOfDocument: null,
  dateOfEvent: null,
  dateReceived: null,
  dateSigned: null,
  clinicName: null,
  companyName: null,
  documentType: null,
  serviceLine: null,
  signatureRequired: null,
  signaturePageNumber: null,
  followUpAttempt: null,
  receivingLocation: null,
  bodyPartLaterality: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  groupId: "",
};
